import React from "react";

import firebase from "firebase/compat/app";
import { useParams } from "react-router";

// MUI
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";

// Locals
import { useCurrentWsDetail, useOrdered } from "../../firebase/hooks";
import { firestoreQuery } from "../../constants";
import { CheckboxMenu, Loading } from "../../components";
import { camelCaseToText } from "../../helpers/format";
import useStyles from "../styles";
import { useUpdateWorkspace } from "../api";

// Types
import type { WorkspaceUser } from "../../users/types";
import type { Option } from "../../components/checkboxMenu/checkboxMenu";
import type { WsParams } from "../../routes";

interface IProps {
  data: Record<string, Record<string, number>>;
  allCount: number;
}

const Statistics: React.FC<IProps> = ({ data, allCount }) => {
  const users = useOrdered<WorkspaceUser>(firestoreQuery.wsUsers);
  const { fields, statisticsToShow = [] } = useCurrentWsDetail();
  const updateWorkspace = useUpdateWorkspace();
  const classes = useStyles();
  const { workspaceId } = useParams<WsParams>();

  const options = React.useMemo<Record<string, Option[]>>(() => {
    if (fields) {
      return Object.entries(fields).reduce((prev, [key, field]) => {
        prev[key] = field.values.map((value) => ({
          title: value,
          value,
          checked: statisticsToShow.some(
            (stat) => stat.type === "count" && stat.field === key && stat.value === value,
          ),
        }));
        return prev;
      }, {} as Record<string, Option[]>);
    }
    return {};
  }, [fields, statisticsToShow]);

  const handleItemSelected = React.useCallback(
    (value: string, checked: boolean, field?: string) => {
      const statistic = {
        type: "count",
        field,
        value,
      };
      if (checked) {
        updateWorkspace(
          {
            statisticsToShow: firebase.firestore.FieldValue.arrayUnion(statistic),
          },
          workspaceId,
        );
      } else {
        updateWorkspace(
          {
            statisticsToShow: firebase.firestore.FieldValue.arrayRemove(statistic),
          },
          workspaceId,
        );
      }
    },
    [workspaceId, updateWorkspace],
  );

  if (!(users && fields)) return <Loading />;

  const activeUserCount = users.reduce((prev, { deactivated }) => {
    if (!deactivated) prev++;
    return prev;
  }, 0);

  return (
    <>
      <Typography
        variant="h6"
        className={classes.interactiveTitle}
        data-cy="workspace-statistics"
      >
        Statistics
        <CheckboxMenu options={options} handleItemSelected={handleItemSelected} />
      </Typography>

      <Table size="small" style={{ marginTop: 16 }}>
        <TableBody>
          <TableRow>
            <TableCell style={{ border: "none", padding: "4px 0", fontWeight: 400 }}>
              Total Items
            </TableCell>
            <TableCell
              style={{ border: "none", padding: "4px 0", textAlign: "right", fontWeight: 600 }}
            >
              {allCount}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell style={{ border: "none", padding: "4px 0", fontWeight: 400 }}>
              Active Users
            </TableCell>
            <TableCell
              style={{ border: "none", padding: "4px 0", textAlign: "right", fontWeight: 600 }}
            >
              {activeUserCount}
            </TableCell>
          </TableRow>

          {Object.entries(data).map(([field, valueCounts]) =>
            Object.entries(valueCounts).map(([value, count]) => (
              <TableRow key={`${field}-${value}`}>
                <TableCell style={{ border: "none", padding: "4px 0", fontWeight: 400 }}>
                  {camelCaseToText(field)}: "{value}"
                </TableCell>
                <TableCell
                  style={{
                    border: "none",
                    padding: "4px 0",
                    textAlign: "right",
                    fontWeight: 600,
                  }}
                >
                  {count}
                </TableCell>
              </TableRow>
            )),
          )}
        </TableBody>
      </Table>
    </>
  );
};

export default Statistics;
